import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconEdit, IconTrash, IconView } from '../components/icons';
import Pagination from '../components/Pagination';
import { useAuth } from '../auth/useAuth';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';
import usePaginatedList from '../hooks/usePaginatedList';

interface Filters {
  search: string;
  asset_class: string;
  status: string;
}

const emptyFilters: Filters = { search: '', asset_class: '', status: '' };

interface InvestmentRow {
  id: number;
  code: string | null;
  name: string;
  asset_class: string;
  amount: string;
  currency_code: string;
  acquisition_date: string;
  maturity_date: string | null;
  expected_return_rate: string | null;
  status: 'active' | 'vacant' | 'matured' | 'demolished' | 'closed' | 'disposed';
  notes: string | null;
}

const STATUS_LABELS: Record<InvestmentRow['status'], string> = {
  active: 'Active',
  vacant: 'Vacant',
  matured: 'Matured',
  demolished: 'Demolished',
  closed: 'Closed',
  disposed: 'Disposed',
};

const STATUS_BADGE: Record<InvestmentRow['status'], string> = {
  active: 'badge-active',
  vacant: 'badge-scheduled',
  matured: 'badge-neutral',
  demolished: 'badge-neutral',
  closed: 'badge-neutral',
  disposed: 'badge-neutral',
};

// The individual investment register — the other half of what used to be
// one "Investment Management" page is now InvestmentPolicy.tsx (the
// asset-allocation breakdown and policy ranges).
export function InvestmentAssets() {
  const { can } = useAuth();
  const { lists, settings } = useConfig();
  const confirmAction = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  // Non-VUV holdings (offshore) show their own currency inline — every
  // other amount in this app is always VUV, so this is the one place a
  // reader needs to be told which currency they're looking at.
  const fmtAmount = (n: number, currency: string) => (currency === 'VUV' ? fmtMoney(n) : `${currency} ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const canCreate = can('investments.create');
  const canUpdate = can('investments.update');
  const canArchive = can('investments.archive');

  const [filters, setFilters] = useState<Filters>(emptyFilters);

  const query = useMemo(
    () => ({
      search: filters.search || undefined,
      asset_class: filters.asset_class || undefined,
      status: filters.status || undefined,
    }),
    [filters]
  );
  const { data, loading, error, setPage, reload } = usePaginatedList<InvestmentRow>('/investments', query);

  const remove = async (row: InvestmentRow) => {
    if (!(await confirmAction(`Delete "${row.name}"?`))) return;
    try {
      await api.delete(`/investments/${row.id}`);
      reload();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Investments</h1>
        {canCreate && (
          <Link className="btn btn-primary" to="/investments/new">
            + Add
          </Link>
        )}
      </header>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by name…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select value={filters.asset_class} onChange={(e) => setFilters({ ...filters, asset_class: e.target.value })}>
          <option value="">All asset classes</option>
          {listOptions(lists, 'investment_asset_class').map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Asset Class</th>
                <th className="right">Amount</th>
                <th>Acquired</th>
                <th>Matures</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="muted center">
                    Loading…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="error center">
                    {error}
                  </td>
                </tr>
              ) : data && data.data.length > 0 ? (
                data.data.map((row) => (
                  <tr key={row.id}>
                    <td className="muted">{row.code ?? '—'}</td>
                    <td>
                      <strong>{row.name}</strong>
                    </td>
                    <td>{labelFor(lists, 'investment_asset_class', row.asset_class)}</td>
                    <td className="right">{fmtAmount(Number(row.amount), row.currency_code)}</td>
                    <td>{row.acquisition_date}</td>
                    <td>{row.maturity_date ?? '—'}</td>
                    <td>
                      <span className={`badge ${STATUS_BADGE[row.status]}`}>{STATUS_LABELS[row.status]}</span>
                    </td>
                    <td className="right">
                      <IconButton icon={IconView} label="View" to={`/investments/${row.id}`} />
                      {canUpdate && <IconButton icon={IconEdit} label="Edit" to={`/investments/${row.id}/edit`} />}
                      {canArchive && (
                        <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(row)} />
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="muted center">
                    No investments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination meta={data} onPage={setPage} />
      </div>
    </>
  );
}
